/**
 * Pure reducer for the live pricing-check progress streamed over the demand SSE
 * channel (HS2-MHCQ1Y). Kept separate from the loader so the accumulation logic
 * — the running counter and the de-duplicated per-line verdict list — is unit
 * testable without a browser EventSource.
 */
import type { AnalysisLiveLine } from './state.js';

export interface AnalysisProgress {
  done: number;
  total: number;
  lines: AnalysisLiveLine[];
}

/** An `analysis.progress` SSE payload (fields optional — the stream is untrusted). */
export interface AnalysisProgressEvent {
  done?: number;
  total?: number;
  lineItemId?: string;
  verdict?: string;
}

/**
 * Fold one `analysis.progress` event into the accumulated progress. A line's
 * verdict is appended only once (events can repeat on reconnect); `done`/`total`
 * come from the event when present, else are derived/carried forward so the
 * counter never regresses to 0 on a partial event.
 */
export function applyAnalysisProgress(prev: AnalysisProgress | null, ev: AnalysisProgressEvent): AnalysisProgress {
  const lines = prev !== null ? [...prev.lines] : [];
  if (
    typeof ev.lineItemId === 'string' &&
    typeof ev.verdict === 'string' &&
    !lines.some((l) => l.lineItemId === ev.lineItemId)
  ) {
    lines.push({ lineItemId: ev.lineItemId, verdict: ev.verdict });
  }
  return {
    done: ev.done ?? lines.length,
    total: ev.total ?? prev?.total ?? 0,
    lines,
  };
}
